const STATE_KEY = 'shopRecommendationContext';
const FIELD = 'product_recommendation_context_token';
const activeContexts = new WeakMap();

/** Attribution belongs to this history entry, outside shared HTML requests and Redis. */
export function recommendationContext(window) {
    const url = new URL(window.location.href);
    if (url.searchParams.has(FIELD)) return null;
    const address = `${url.pathname}${url.search}`;
    const fragment = new URLSearchParams(url.hash.slice(1));
    const previous = window.history.state?.[STATE_KEY];
    const incoming = fragment.has('rec');
    let token = incoming
        ? fragment.getAll('rec').length === 1
            ? fragment.get('rec')
            : ''
        : previous?.address === address
          ? previous.token
          : '';
    if (
        typeof token !== 'string' ||
        token.length > 4096 ||
        !/^[A-Za-z0-9_-]+\.[a-f0-9]{64}$/.test(token)
    )
        token = '';
    if (!incoming && !token) {
        if (activeContexts.has(window)) clearInput();
        activeContexts.delete(window);
        return null;
    }
    if (incoming) {
        url.hash = url.hash
            .slice(1)
            .split('&')
            .filter((part) => {
                try {
                    return decodeURIComponent(part.split('=', 1)[0]) !== 'rec';
                } catch {
                    return true;
                }
            })
            .join('&');
    }
    const existing = activeContexts.get(window);
    const marker =
        !incoming && existing?.address === address && existing.token === token
            ? existing
            : { address, token };
    activeContexts.set(window, marker);
    function isCurrent() {
        return (
            activeContexts.get(window) === marker &&
            `${window.location.pathname}${window.location.search}` === address
        );
    }
    function remember(value, href = url.href) {
        const state = Object.assign({}, window.history.state);
        if (value) state[STATE_KEY] = { address, token: value };
        else delete state[STATE_KEY];
        try {
            window.history.replaceState(state, '', href);
        } catch {
            /* Navigation and cart submission remain available without history access. */
        }
    }
    function clearInput() {
        window.document.querySelector(`#product-form input[name="${FIELD}"]`)?.remove();
    }
    // Save before removing the fragment: a failed handshake stays retryable after reload.
    remember(token);
    if (incoming || marker !== existing) {
        // A new candidate must never submit the previous, already validated context.
        clearInput();
    }
    return {
        token,
        url: url.href,
        commit(acceptedToken) {
            if (!isCurrent()) return;
            const current = window.history.state?.[STATE_KEY];
            if (current?.address === address && current.token !== token) return;
            const accepted = token && acceptedToken === token ? token : '';
            remember(accepted, window.location.href);
            if (!accepted) {
                clearInput();
                activeContexts.delete(window);
                return;
            }
            const apply = () => {
                if (!isCurrent()) return;
                const form = window.document.querySelector('#product-form');
                if (!form) return;
                let input = form.querySelector(`input[name="${FIELD}"]`);
                if (!input) {
                    input = window.document.createElement('input');
                    input.type = 'hidden';
                    input.name = FIELD;
                    form.append(input);
                }
                input.value = accepted;
            };
            if (window.document.readyState === 'loading')
                window.document.addEventListener('DOMContentLoaded', apply, { once: true });
            else apply();
        },
    };
}

/** Same-document history changes do not run the page bootstrap again. */
export function followRecommendationNavigation(window, config) {
    const refresh = () => {
        const next = recommendationContext(window);
        if (!next) return;
        const initialize = () => {
            initializePrivateRecommendation(window, config, next, true);
        };
        void Promise.resolve(window.shopInitialization).then(initialize, initialize);
    };
    window.addEventListener('hashchange', () => {
        if (new URLSearchParams(window.location.hash.slice(1)).has('rec')) refresh();
    });
    window.addEventListener('popstate', refresh);
}

/** Private product documents validate context without recording a second page view. */
export function initializePrivateRecommendation(window, config, context, force = false) {
    const productId =
        config.recommendationInitialization?.productId ||
        Number(window.document.querySelector('#product-form input[name="product_id"]')?.value);
    if (!context || !productId || (window.shopInitialization && !force)) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2500);
    window.shopInitialization = window
        .fetch('/index.php?route=common/activity/recommendation', {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
                product_id: productId,
                token: config.serverTracking?.token || '',
                recommendation_context_token: context.token,
            }),
        })
        .then((response) => (response.ok ? response.json() : null))
        .then((result) => {
            if (result?.success) context.commit(result.recommendationContextToken);
        })
        .catch(() => {})
        .finally(() => window.clearTimeout(timer));
}
